<template>
  <div class="c-height-input flex flex-col gap-2">
    <NCheckbox v-model:checked="isHeightSet" @update:checked="handleCheckboxChange">
      {{ t('canvasUI.components.CHeightInput.setFixedHeight') }}
    </NCheckbox>

    <div v-if="isHeightSet" class="flex gap-2">
      <NInputNumber
        v-model:value="heightValue"
        class="flex-1"
        :step="heightUnit === 'px' ? 10 : 1"
        @update:value="handleHeightChange"
      />
      <CSelect
        v-model:value="heightUnit"
        bordered
        class="w-[100px]"
        :options="unitOptions"
        @update:value="handleUnitChange"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { NCheckbox, NInputNumber } from 'naive-ui'
import CSelect from '@lib/components/CSelect/CSelect.vue'

type HeightUnit = 'px' | 'vh' | 'em' | 'rem'

const props = defineProps<{
  defaultValue?: string
  modelValue?: string | null
}>()

const emit = defineEmits<{
  'update:modelValue': [value: string | null | undefined]
}>()

const { t } = useI18n()

const isHeightSet = ref(false)
const heightValue = ref<number | null>(null)
const heightUnit = ref<HeightUnit>('px')

const unitOptions = [
  { label: 'px', value: 'px' },
  { label: 'vh', value: 'vh' },
  { label: 'em', value: 'em' },
  { label: 'rem', value: 'rem' },
] as const

function parseHeight(value?: string): { value: number; unit: HeightUnit } {
  if (!value) return { value: 0, unit: 'px' }

  const match = value.match(/^(\d+(?:\.\d+)?)(px|vh|em|rem)?$/)
  if (!match) return { value: 0, unit: 'px' }

  const numValue = parseFloat(match[1])
  const unit = (match[2] ?? 'px') as HeightUnit

  return { value: numValue, unit }
}

function handleCheckboxChange(checked: boolean) {
  if (!checked) {
    emit('update:modelValue', null)
  } else {
    if (props.defaultValue) {
      const { value, unit } = parseHeight(props.defaultValue)
      heightValue.value = value
      heightUnit.value = unit
      emit('update:modelValue', props.defaultValue)
    } else {
      emit('update:modelValue', `${heightValue.value ?? 0}${heightUnit.value}`)
    }
  }
}

function handleHeightChange(value: number | null) {
  heightValue.value = value
  if (value !== null) {
    emit('update:modelValue', `${value}${heightUnit.value}`)
  }
}

function handleUnitChange(unit: HeightUnit) {
  heightUnit.value = unit
  if (heightValue.value !== null) {
    emit('update:modelValue', `${heightValue.value}${unit}`)
  }
}

onMounted(() => {
  if (props.modelValue) {
    const { value, unit } = parseHeight(props.modelValue)
    isHeightSet.value = true
    heightValue.value = value
    heightUnit.value = unit
  }
})
</script>
